import React from 'react';
import {PropTypes} from 'prop-types';
import moment from 'moment-timezone';
import numeral from 'numeral';
import {ErrorsBlockComponent} from '../../shared/errorsBlockComponent';
import {ButtonBusyIndicatorComponent} from '../../shared/buttonBusyIndicatorComponent';
import {Link} from '../../shared/link';
import {Pagination} from '../../shared/Pagination';
import {PageHeader} from '../../shared/PageHeader';
import {MakeApiCall, WriteConsoleErrors, ToString, ObjectToQueryString, ReplaceUrl} from '../../shared/utils';
import {SimpleFilterItemComponent} from '../SimpleFilterItem';
import {UrlBuilder} from '../../app.defs';

import './styles.scss';

const AFFILIATES_ACTION = {
    NONE: 0,
    LOAD: 1
}

export class AffiliatesListComponent extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);
        this.state = {
            isLoaded: false,
            affiliateFilter: props.affiliateFilter,
            siteId: props.siteId || 'any',
            pageNum: props.pageNum,
            pageSize: props.pageSize,
            action: AFFILIATES_ACTION.NONE,
            totalCount: 0,
            errors: null,
            affiliates: [],
            requestId: 0
        }

        this.doLoad = this.doLoad.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.renderForm = this.renderForm.bind(this);
        this.renderTable = this.renderTable.bind(this);
        this.renderPagination = this.renderPagination.bind(this);
    }

    componentDidMount() {
        const self = this;

        self._isMounted = true;
        self.doLoad();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    doLoad() {
        const self = this;
        if (!self._isMounted) {
            return;
        }

        const requestId = self.state.requestId + 1;

        const data = {
            afff: self.state.affiliateFilter,
            site: self.state.siteId,
            pn: self.state.pageNum,
            ps: self.state.pageSize
        }

        new Promise(resolve => {
            self.setState({action: AFFILIATES_ACTION.LOAD, errors: null, requestId: requestId}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                ReplaceUrl({
                    pathname: window.location.pathname,
                    search: ObjectToQueryString(data, true, {pn:1, ps: 50, siteId: 'any'})
                });

                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getAffiliates',
                            data: data,
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted || (self.state.requestId != requestId)
                );
            }
        })
        .then(response => {
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({
                    action: AFFILIATES_ACTION.NONE,
                    isLoaded: true,
                    affiliateFilter: response.afff,
                    pageNum: response.pn,
                    pageSize: response.ps,
                    affiliates: response.Affiliates,
                    totalCount: response.TotalCount
                });
            }
        })
        .catch(errors => {
            WriteConsoleErrors(errors, true, false);
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({action: AFFILIATES_ACTION.NONE, errors: errors})
            }
        });
    }

    handleSubmit(event) {
        event.preventDefault();
        this.doLoad();
    }

    renderForm() {
        const self = this;
        const {action, affiliateFilter, siteId} = self.state;
        const isDisabled = action != AFFILIATES_ACTION.NONE;

        const _renderFilterLink = (filter, negativeFilter, filterPattern) => {
            return (
                <SimpleFilterItemComponent
                    disabled={isDisabled}
                    filters={affiliateFilter}
                    filter={filter}
                    detectPattern={filterPattern}
                    negativeDetectPattern={negativeFilter}
                    onChange={f => self.setState({affiliateFilter: f})}
                />
            )
        }

        return (
            <div className="mb-n2 mt-2">
                <form className="form-inline" onSubmit={self.handleSubmit}>
                    <input 
                        type="text"
                        className="form-control mr-2 mb-2"
                        placeholder="AffiliateId, Email, Name :filter"
                        style={{width: '400px', maxWidth: '100%'}}
                        disabled={isDisabled}
                        value={ToString(affiliateFilter)}
                        onChange={e => self.setState({affiliateFilter: e.target.value})}
                    />
                    <select
                        className="custom-select mr-2 mb-2"
                        disabled={isDisabled}
                        value={ToString(siteId)}
                        onChange={e => self.setState({siteId: e.target.value})}
                    >
                        {self.props.sites.map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                    </select>
                    <button 
                        type="submit"
                        className="btn btn-outline-primary mb-2"
                        disabled={isDisabled}
                    >
                        <span className="fa fa-search mr-1" />
                        Search
                        <ButtonBusyIndicatorComponent visible={action == AFFILIATES_ACTION.LOAD} />
                    </button>
                </form>
                <div className="small">
                    Filters:&nbsp;
                    <span className="mre-1 text-nowrap d-inline-block">{_renderFilterLink(':lock', ':nlock')} — locked only;</span>
                    <span className="mre-1 text-nowrap d-inline-block">{_renderFilterLink(':nlock', ':lock')} — not locked only;</span>
                    <span className="mre-1 text-nowrap d-inline-block">{_renderFilterLink(':com')} — with commission;</span>
                    <span className="mre-1 text-nowrap d-inline-block">{_renderFilterLink(':pay')} — with payout;</span>
                    {/* <span className="mre-1 text-nowrap d-inline-block">{_renderFilterLink(':auto', ':nauto')}, {_renderFilterLink(':nauto', ':auto')} — auto or not;</span> */}
                </div>
            </div>
        )
    }

    renderTable() {
        const self = this;
        const {action, affiliates} = self.state;
        const {timeZoneId} = self.props;
        
        const tzNow = moment.utc().tz(timeZoneId);
        const tzName = tzNow.format('z');
        const existsNew = affiliates.some(p => tzNow.isSame(moment.utc(p.CreatedAt).tz(timeZoneId), 'day'));

        return (
            <table className="affiliates table-striped-even table-hover table-responsive newstyle">
                <thead>
                    <tr>
                        {existsNew && <th width="1%" />}
                        <th width="1%">Affiliate#</th>
                        <th width="1%">Site</th>
                        <th width="1%" className="text-nowrap">Created ({tzName})</th>
                        <th width="25%" className="text-nowrap">Name</th>
                        <th width="25%" className="text-nowrap">Email</th>
                        <th width="5%" className="text-nowrap">Commission %</th>
                        <th width="5%" className="text-nowrap">Page Views</th>
                        <th width="5%" className="text-nowrap">Visitors</th>
                        <th width="5%" className="text-nowrap">Orders</th>
                        <th width="5%" className="text-nowrap">Amount</th>
                        <th width="5%" className="text-nowrap">Commission</th>
                        <th width="5%" className="text-nowrap">Payout</th>
                    </tr>
                </thead>
                <tbody>
                    {affiliates.map(p => {
                        let rowClass = '';

                        if (Boolean(p.LockedAt)) {
                            rowClass += 'danger'
                        }

                        return (
                            <tr key={'affiliate-' + p.Id} className={rowClass}>
                                {existsNew && 
                                    <td>
                                        {tzNow.isSame(moment.utc(p.CreatedAt).tz(timeZoneId), 'day') && <span className="fa fa-flash text-success" />}
                                    </td>
                                }
                                <td className="text-nowrap">
                                    <Link 
                                        to={UrlBuilder.AffiliateInfo({AffiliateId: p.Id})}
                                        className="mre-2"
                                    >
                                        {p.Id}
                                    </Link>
                                </td>
                                <td className="text-nowrap">{p.SiteName}</td>
                                <td className="text-nowrap">{moment.utc(p.CreatedAt).tz(timeZoneId).format('L LT')}</td>
                                <td className="text-nowrap">{p.Name}</td>
                                <td className="text-nowrap">{p.Email}</td>
                                <td className="text-nowrap">{numeral(p.CommissionPercent).PERCENT()}</td>
                                <td className="text-right text-nowrap">{p.PageViewsCount}</td>
                                <td className="text-right text-nowrap">{p.VisitorsCount}</td>
                                <td className="text-right text-nowrap">{p.OrdersCount}</td>
                                <td className={'text-right text-nowrap' + (p.OrdersAmount < 0 ? ' text-danger' :'')}>
                                    {numeral(p.OrdersAmount).USD()}
                                </td>
                                <td className={'text-right text-nowrap' + (p.CommissionAmount < 0 ? ' text-danger' :'')}>
                                    {numeral(p.CommissionAmount).USD()}
                                </td>
                                <td className={'text-right text-nowrap' + (p.PayoutAmount < 0 ? ' text-danger' :'')}>
                                    {numeral(p.PayoutAmount).USD()}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        )
    }

    renderPagination() {
        const self = this;
        const {pageNum, pageSize, totalCount}  = self.state;

        const pagesCount = Math.ceil(totalCount / Math.max(1, pageSize));

        if (pagesCount <= 1) {
            return null;
        }

        return (
            <Pagination
                className="justify-content-end"
                pagesCount={pagesCount}
                pageNum={pageNum}
                maxButtons={7}
                onPageSelect={(pn) => self.setState({pageNum: pn}, self.doLoad)}
                size="sm"
            />
        )
    }

    render() {
        const self = this;
        const {totalCount, isLoaded, errors} = self.state;

        return (
            <div>
                <PageHeader
                    fixed
                    captionTruncate
                    notWrappedMaxHeight={null}
                    caption={
                        <h2 className="text-truncate">Affiliates</h2>
                    }
                >
                    {self.renderForm()}
                </PageHeader>
                <ErrorsBlockComponent errors={errors} />
                {isLoaded && <div className="small">{`${totalCount} affiliate(s) found`}</div>}
                {self.renderTable()}
                <div className="pt-3">
                    {self.renderPagination()}
                </div>
           </div>
        )
    }
}
